import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * Componente JsonEditor profissional com numeração de linhas e resize
 * @param {string} value - Conteúdo do editor
 * @param {function} onChange - Callback quando o conteúdo muda
 * @param {number} minHeight - Altura mínima (default: 150)
 * @param {number} maxHeight - Altura máxima (default: 400)
 * @param {string} placeholder - Placeholder do editor
 * @param {boolean} showToolbar - Mostrar barra de ferramentas (default: true)
 * @param {string} label - Label da barra de ferramentas (default: 'JSON Payload')
 * @param {React.Ref} textareaRef - Ref externo para o textarea (para inserção de variáveis)
 */
const JsonEditor = ({
    value,
    onChange,
    minHeight = 150,
    maxHeight = 400,
    placeholder = '',
    showToolbar = true,
    label = 'JSON Payload',
    textareaRef: externalTextareaRef
}) => {
    const internalTextareaRef = useRef(null);
    const textareaRef = externalTextareaRef || internalTextareaRef;
    const lineNumbersRef = useRef(null);
    const containerRef = useRef(null);
    const [height, setHeight] = useState(minHeight);
    const [isResizing, setIsResizing] = useState(false);
    const [jsonError, setJsonError] = useState(null);

    // Calcular número de linhas
    const lines = (value || '').split('\n');
    const lineCount = lines.length;

    // Validar JSON
    useEffect(() => {
        if (!value || !value.trim()) {
            setJsonError(null);
            return;
        }
        try {
            JSON.parse(value);
            setJsonError(null);
        } catch (e) {
            setJsonError(e.message);
        }
    }, [value]);

    // Sincronizar scroll
    const handleScroll = useCallback((e) => {
        if (lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = e.target.scrollTop;
        }
    }, []);

    // Formatar JSON
    const formatJson = useCallback(() => {
        try {
            const parsed = JSON.parse(value);
            const formatted = JSON.stringify(parsed, null, 2);
            onChange(formatted);
        } catch (e) {
            // Ignorar erro de formatação
        }
    }, [value, onChange]);

    // Copiar para clipboard
    const copyToClipboard = useCallback(() => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(value);
        }
    }, [value]);

    // Handle resize
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const newHeight = e.clientY - rect.top;
            setHeight(Math.max(minHeight, Math.min(maxHeight, newHeight)));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, minHeight, maxHeight]);

    return (
        <Box ref={containerRef} sx={{ position: 'relative' }}>
            {/* Toolbar */}
            {showToolbar && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px 4px 0 0',
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderBottom: '1px solid #ccc'
                }}>
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
                        {label}
                    </Typography>
                    <Box>
                        <Tooltip title="Formatar JSON">
                            <IconButton size="small" onClick={formatJson} sx={{ color: '#666', '&:hover': { color: '#333' } }}>
                                <FormatAlignLeftIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Copiar">
                            <IconButton size="small" onClick={copyToClipboard} sx={{ color: '#666', '&:hover': { color: '#333' } }}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {/* Editor Container */}
            <Box sx={{
                display: 'flex',
                backgroundColor: '#fafafa',
                borderRadius: showToolbar ? '0 0 4px 4px' : '4px',
                overflow: 'hidden',
                height: height,
                position: 'relative',
                border: '1px solid #ddd',
                borderTop: showToolbar ? 'none' : '1px solid #ddd'
            }}>
                {/* Line Numbers */}
                <Box
                    ref={lineNumbersRef}
                    sx={{
                        width: '40px',
                        backgroundColor: '#f0f0f0',
                        color: '#999',
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: '12px',
                        lineHeight: '1.5',
                        padding: '8px 4px',
                        textAlign: 'right',
                        userSelect: 'none',
                        overflow: 'hidden',
                        borderRight: '1px solid #ddd'
                    }}
                >
                    {Array.from({ length: lineCount }, (_, i) => (
                        <div key={i + 1} style={{ height: '18px' }}>{i + 1}</div>
                    ))}
                </Box>

                {/* Textarea */}
                <textarea
                    ref={textareaRef}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={handleScroll}
                    placeholder={placeholder}
                    spellCheck={false}
                    style={{
                        flex: 1,
                        backgroundColor: '#fafafa',
                        color: '#333',
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: '12px',
                        lineHeight: '1.5',
                        padding: '8px',
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        overflow: 'auto',
                        whiteSpace: 'pre',
                        tabSize: 2
                    }}
                />

                {/* Resize Handle */}
                <Box
                    onMouseDown={handleMouseDown}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        background: 'linear-gradient(135deg, transparent 50%, #bbb 50%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, transparent 50%, #999 50%)'
                        }
                    }}
                />
            </Box>

            {/* JSON Error */}
            {jsonError && (
                <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: '11px' }}>
                    JSON inválido: {jsonError}
                </Alert>
            )}
        </Box>
    );
};

export default JsonEditor;
